import { useState } from 'react';
import PhaseBadge from '@/components/feature/PhaseBadge';
import { PHASES, type PhaseStatus } from '@/mocks/schedule';
import { formatCnRange } from '@/utils/date';

type FilterKey = 'all' | PhaseStatus;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: '全部阶段' },
  { key: 'active', label: '进行中' },
  { key: 'upcoming', label: '未开始' },
  { key: 'done', label: '已结束' },
];

export default function TimelineSection() {
  const [filter, setFilter] = useState<FilterKey>('all');
  const list = filter === 'all' ? PHASES : PHASES.filter((p) => p.status === filter);

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50" aria-labelledby="timeline-heading">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <h2 id="timeline-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              阶段时间轴
            </h2>
            <p className="mt-3 text-sm text-foreground-600">
              共 {PHASES.length} 个阶段，按时间顺序排列。筛选后仅展示对应状态的阶段。
            </p>
          </div>

          <div className="inline-flex flex-wrap gap-1 px-1 py-1 rounded-full bg-background-100 border border-background-200 self-start">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                aria-pressed={filter === f.key}
                className={`px-4 py-2 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  filter === f.key
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {list.length === 0 ? (
          <p className="mt-10 rounded-lg border border-background-200 bg-background-100 px-6 py-10 text-center text-sm text-foreground-500">
            当前没有处于该状态的阶段，切换到「全部阶段」查看完整赛程。
          </p>
        ) : (
          <ol className="mt-10 relative">
            <span className="absolute left-[19px] md:left-[23px] top-2 bottom-2 w-px timeline-rail" aria-hidden="true"></span>
            {list.map((p) => {
              const index = PHASES.findIndex((x) => x.id === p.id) + 1;
              return (
                <li key={p.id} className="relative flex gap-4 md:gap-6 pb-8 last:pb-0">
                  <span
                    className={`relative z-10 w-10 h-10 md:w-12 md:h-12 shrink-0 flex items-center justify-center rounded-full font-heading font-bold ${
                      p.status === 'active'
                        ? 'bg-primary-500 text-background-50'
                        : p.status === 'done'
                          ? 'bg-background-200 text-foreground-600'
                          : 'bg-background-50 border border-secondary-300 text-secondary-700'
                    }`}
                  >
                    {index}
                  </span>

                  <article className="min-w-0 flex-1 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">{p.name}</h3>
                      <PhaseBadge status={p.status} />
                      <span className="text-xs text-foreground-500">{p.tag}</span>
                    </div>

                    <p className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-primary-700">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-calendar-line"></i>
                      </span>
                      {formatCnRange(p.start, p.end)}
                    </p>

                    <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{p.summary}</p>

                    <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                      {p.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-2 text-sm text-foreground-600">
                          <span className="w-4 h-4 shrink-0 flex items-center justify-center mt-1">
                            <i className="ri-checkbox-circle-line text-secondary-500"></i>
                          </span>
                          {pt}
                        </li>
                      ))}
                    </ul>

                    <p className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent-100 border border-accent-300 px-3 py-2 text-xs md:text-sm text-accent-900">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-alarm-warning-line"></i>
                      </span>
                      {p.action}
                    </p>
                  </article>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}